import { Either, Maybe } from 'monet';
import LeftResponse from '@/storage/LeftResponse';

/**
 * @description Запрос на формирование справки НДФЛ.
 * @constructor
 */
export function FormNdflCertificateRequest(
  amountByExpensive,
  amountBySimple,
  certificateNumber,
  correctionNumber,
  guardianBirthDate,
  guardianIdentificationCode,
  guardianIdentificationSerialAndNumber,
  guardianIdentificationWhenIssued,
  guardianInn,
  guardianName,
  guardianOnlyInn,
  guardianPatronymic,
  guardianSurname,
  isSinglePerson,
  issuedDate,
  patientBirthDate,
  patientIdentificationCode,
  patientIdentificationSerialAndNumber,
  patientIdentificationWhenIssued,
  patientInn,
  patientName,
  patientOnlyInn,
  patientPatronymic,
  patientSurname,
  positionIds,
  reportingYear,
  reportingType,
  selectedClinicId,
  signerId,
) {
  /**
   * @description Форматировать дату.
   * @param {string} candidate
   * @returns {string}
   */
  const formatDate = (candidate) => {
    if (candidate) {
      return candidate.split('.').reverse().join('-');
    }
    return '';
  };

  /**
   * @description Получить дату выдачи.
   * @returns {string}
   */
  this.getIssuedDate = () => formatDate(issuedDate);

  /**
   * @description К копейкам.
   * @param {string} rub
   * @returns {number}
   */
  const toKop = (rub) => +rub * 100;

  /**
   * @description К запросу.
   * @returns {object}
   */
  this.toQuery = () => ({
    amount_by_expensive: toKop(amountByExpensive),
    amount_by_simple: toKop(amountBySimple),
    certificate_number: certificateNumber,
    correction_number: correctionNumber,
    guardian_birth_date: formatDate(guardianBirthDate),
    guardian_identification_code: guardianIdentificationCode,
    guardian_identification_serial_and_number: guardianIdentificationSerialAndNumber,
    guardian_identification_when_issued: formatDate(guardianIdentificationWhenIssued),
    guardian_inn: guardianInn,
    guardian_name: guardianName,
    guardian_only_inn: guardianOnlyInn,
    guardian_patronymic: guardianPatronymic,
    guardian_surname: guardianSurname,
    is_single_person: isSinglePerson,
    issued_date: formatDate(issuedDate),
    patient_birth_date: formatDate(patientBirthDate),
    patient_identification_code: patientIdentificationCode,
    patient_identification_serial_and_number: patientIdentificationSerialAndNumber,
    patient_identification_when_issued: formatDate(patientIdentificationWhenIssued),
    patient_inn: patientInn,
    patient_name: patientName,
    patient_only_inn: patientOnlyInn,
    patient_patronymic: patientPatronymic,
    patient_surname: patientSurname,
    position_ids: positionIds,
    reporting_year: reportingYear,
    reporting_type: reportingType,
    selected_clinic_id: selectedClinicId,
    signer_id: signerId,
  });
}

/**
 * @description Шлюз реестра справок НДФЛ.
 * @param {Axios} axios
 */
export default function RegistryNdflCertificateGateway(axios) {
  /**
   * @description Получить справку по идентификатору.
   * @param {number} certificateId Идентификатор справки.
   * @returns {Promise<Either>}
   */
  this.viewById = (certificateId) => axios
    .get(`/api/registry/ndfl-certificates/${certificateId}`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && error.response.status === 404) {
          const { causes, title } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );

  /**
   * @description Получить список справок для просмотра.
   * @returns {Promise<Maybe>}
   */
  this.viewList = (filters) => {
    const params = {
      patient_id: filters.patientId,
      employee_id: filters.employeeId,
      period: filters.period || null,
      sort: filters.sort || null,
    };
    return axios
      .get('/api/registry/ndfl-certificates', { params })
      .then(
        async (response) => Either.right(response.data.data),
        async (error) => {
          if (error.isAxiosError) {
            return Either.left(error.response.data.title);
          }
          throw error;
        },
      );
  };

  /**
   * @description Получить опции.
   * @param {number} templateId Идентификатор.
   * @returns {Promise<Validation>}
   */
  this.viewOptions = () => axios
    .get('/api/registry/ndfl-certificate-options')
    .then(async (response) => Maybe.fromEmpty(response.data.data));

  /**
   * @description Показать опции формирования справки пациента для налоговой.
   * @returns {Promise<Either>}
   */
  this.viewFormOptions = (patientId) => axios
    .get(`/api/patients/${patientId}/ndfl-certificate-options`)
    .then(
      (response) => Either.right(response.data.data),
      (error) => {
        if (error.isAxiosError && error.response && [400, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );

  /**
   * @description Получить данные для заполнения справки НДФЛ.
   * @param {number}  patientId Идентификатор пациента.
   * @param {string}  clinicId  Выбранная клиника.
   * @param {number}  year      Отчетный год.
   * @param {string}  type      Тип сертификата.
   * @returns {Promise<Maybe>}
   */
  this.viewFillers = (patientId, clinicId, year = 2024, type = 'doc') => axios
    .get(
      `/api/patients/${patientId}/ndfl-certificate-fillers/${year}`,
      { params: { type, clinic_id: clinicId } },
    )
    .then(
      async (response) => {
        const { data } = response;
        return Maybe.fromEmpty({
          fillers: data.data,
          identificationCodeOptions: data.identification_code_options,
          invoices: data.invoices,
        });
      },
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          const leftResponse = new LeftResponse([], title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на формирование справки НДФЛ.
   * @param {FormNdflCertificateRequest} request Запрос.
   * @returns {Promise<Either>}
   */
  this.formCertificate = (patientId, request) => axios
    .post(`/api/patients/${patientId}/ndfl-certificates`, request.toQuery())
    .then(
      async (response) => Either.right(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const response = new LeftResponse(causes, title);
          return Either.left(response);
        }
        throw error;
      },
    );

  /**
   * @description Удалить по идентификатору.
   * @param {number} certificateId
   * @returns {Promise<Either>}
   */
  this.deleteById = (certificateId) => axios
    .delete(`/api/registry/ndfl-certificates/${certificateId}/delete`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [403, 404].includes(error.response.status)) {
          const json = await (error.response.data || '').text();
          const data = JSON.parse(json);
          return Either.left(data);
        }
        throw error;
      },
    );

  /**
   * @description Скачать по идентификатору.
   * @param {number} certificateId Идентификатор справки.
   * @returns {Promise<Either>}
   */
  this.downloadById = (certificateId) => axios
    .get(
      `/api/registry/ndfl-certificates/${certificateId}/download`,
      { responseType: 'blob' },
    )
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [403, 404].includes(error.response.status)) {
          const json = await (error.response.data || '').text();
          const data = JSON.parse(json);
          return Either.left(data);
        }
        throw error;
      },
    );

  /**
   * @description Распечатать по идентификатору.
   * @param {number} certificateId Идентификатор справки.
   * @returns {Promise<Either>}
   */
  this.printById = (certificateId) => axios
    .get(
      `/api/registry/ndfl-certificates/${certificateId}/download`,
      { responseType: 'blob', headers: { accept: 'application/pdf' } },
    )
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [403, 404].includes(error.response.status)) {
          const json = await (error.response.data || '').text();
          const data = JSON.parse(json);
          return Either.left(data);
        }
        throw error;
      },
    );
}
